import { useLocation, Link } from "react-router-dom";
import { useUserRole } from "@/hooks/useUserRole";
import {
  LayoutDashboard,
  Upload,
  Settings,
  FileText,
  BarChart,
  MousePointer2,
  Webhook,
  Globe,
  Search,
  ScanSearch,
  type LucideIcon,
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { GscSidebarNav } from "@/components/gsc/GscSidebarNav";
import { cn } from "@/lib/utils";

interface SidebarContentProps {
  onClose: () => void;
}

interface SidebarMenuItem {
  path: string;
  label: string;
  icon: LucideIcon;
  protected?: boolean;
}

export const SidebarContent = ({ onClose }: SidebarContentProps) => {
  const location = useLocation();
  const { isAdmin, isLoading } = useUserRole();

  const menuItemsBeforeGsc: SidebarMenuItem[] = [
    { path: "/", label: "Dashboard", icon: LayoutDashboard },
    { path: "/analytics", label: "Analytics", icon: BarChart },
    { path: "/clicks", label: "Clicks", icon: MousePointer2 },
    { path: "/indexation", label: "Indexation", icon: ScanSearch },
  ];

  const menuItemsAfterGsc: SidebarMenuItem[] = [
    { path: "/settings", label: "Settings", icon: Settings, protected: true },
    { path: "/import", label: "Import CSV", icon: Upload },
    { path: "/keyword-volume", label: "Keyword Volume", icon: Search },
    { path: "/spy-dashboard", label: "SpySerp Dashboard", icon: Globe },
    { path: "/spy-import", label: "Spy Import", icon: Webhook },
    { path: "/reports", label: "Reports", icon: FileText },
  ];

  const renderMenuItem = (item: SidebarMenuItem) => {
    const isActive = location.pathname === item.path;

    if (item.protected) {
      if (isLoading) {
        return (
          <div key={item.path} className="flex justify-center px-2">
            <Skeleton className="h-10 w-10 rounded-md bg-sidebar-accent/80" />
          </div>
        );
      }
      if (!isAdmin) return null;
    }

    return (
      <Link
        key={item.path}
        to={item.path}
        onClick={onClose}
        title={item.label}
        className={cn(
          "relative flex w-full items-center justify-center py-4 transition-colors duration-200",
          isActive
            ? "bg-sidebar-accent text-sidebar-foreground"
            : "text-sidebar-foreground/55 hover:bg-sidebar-accent/40 hover:text-sidebar-foreground",
        )}
      >
        {isActive && (
          <div
            className="absolute top-0 bottom-0 left-0 w-1 rounded-r-full bg-[var(--chart-2)]"
            aria-hidden
          />
        )}
        <item.icon
          className={cn("size-6", isActive ? "stroke-[2.5px]" : "stroke-[2px]")}
        />
      </Link>
    );
  };

  return (
    <div className="dark flex h-full min-h-0 w-full flex-1 flex-col">
      <div className="flex items-center justify-center p-4">
        <Link
          to="/"
          className="flex size-8 items-center justify-center rounded-lg motion-safe:transition-transform motion-safe:hover:scale-105 motion-safe:active:scale-95"
          aria-label="oracul.site home"
        >
          <img src="/oracul-mark.svg" alt="" width={32} height={32} className="size-8" draggable={false} />
        </Link>
      </div>

      <nav className="flex min-h-0 flex-1 flex-col border-t border-sidebar-border py-4">
        {menuItemsBeforeGsc.map(renderMenuItem)}
        <GscSidebarNav onClose={onClose} />
        {menuItemsAfterGsc.map(renderMenuItem)}
      </nav>
    </div>
  );
};
